import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { getTicketList, getTicketDashboard } from '../../../store/actions/ticketAction';
import { apiRequest } from '../../../services/apiService';
import CreateTicketModal from './CreateTicketModal';
import SurveyListHeader from '../../../component/common/layout/SurveyListHeader';
import SurveyListSidebar from '../../../component/common/layout/SurveyListSidebar';

const SEVERITY_COLORS = {
    low: '#6c757d',
    medium: '#ffc107',
    high: '#fd7e14',
    critical: '#dc3545',
};

const STATUS_COLORS = {
    open: '#dc3545',
    acknowledged: '#17a2b8',
    in_progress: '#ffc107',
    resolved: '#28a745',
    false_positive: '#6c757d',
};

const STATUS_KEYS = {
    open: 'tickets.status.open',
    acknowledged: 'tickets.status.acknowledged',
    in_progress: 'tickets.status.in_progress',
    resolved: 'tickets.status.resolved',
    false_positive: 'tickets.status.false_positive',
};

const ALERT_TYPE_KEYS = {
    brute_force: 'tickets.alert_type.brute_force',
    unauthorized_token: 'tickets.alert_type.unauthorized_token',
    unknown_user_login: 'tickets.alert_type.unknown_user',
    inactive_user_login: 'tickets.alert_type.inactive_user',
    suspicious_ip: 'tickets.alert_type.suspicious_ip',
    after_hours_access: 'tickets.alert_type.after_hours',
    manual: 'tickets.alert_type.manual',
};

const Badge = ({ color, children }) => (
    <span style={{
        backgroundColor: color,
        color: '#fff',
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </span>
);

const DashboardCard = ({ label, value, color }) => (
    <div style={{
        flex: '1 1 0',
        minWidth: 140,
        background: '#fff',
        borderRadius: 8,
        padding: '16px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderLeft: `4px solid ${color}`,
    }}>
        <div style={{ fontSize: 13, color: '#728095', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: '#0F2744' }}>{value}</div>
    </div>
);

export default function TicketList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { tickets, loading, dashboard, dashboardLoading } = useSelector((state) => state.ticketData);

    const [statusFilter, setStatusFilter] = useState('');
    const [severityFilter, setSeverityFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [assignedToFilter, setAssignedToFilter] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [users, setUsers] = useState([]);

    const M7 = window.modulePermissions?.M7 || {};

    useEffect(() => {
        if (M7.view_all == 1) {
            loadUsers();
        }
    }, []);

    useEffect(() => {
        dispatch(getTicketList({
            status: statusFilter || null,
            severity: severityFilter || null,
            source: sourceFilter || null,
            assigned_to: assignedToFilter || null,
        }));
        if (M7.view_all == 1) {
            dispatch(getTicketDashboard());
        }
    }, [statusFilter, severityFilter, sourceFilter, assignedToFilter]);

    const loadUsers = async () => {
        const { response } = await apiRequest({
            method: 'POST',
            url: '/users/getUserList',
            data: { startingLimit: 0, resultsPerPage: 500, search: '' },
        });
        if (response?.data?.data) {
            setUsers(response.data.data);
        }
    };

    const filteredTickets = useMemo(() => {
        if (!searchText) return tickets;
        const lower = searchText.toLowerCase();
        return tickets.filter(t =>
            t.title?.toLowerCase().includes(lower) ||
            t.assigned_to_name?.toLowerCase().includes(lower) ||
            t.raised_by_name?.toLowerCase().includes(lower)
        );
    }, [tickets, searchText]);

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        toast.success(t('tickets.created_success'));
        dispatch(getTicketList({
            status: statusFilter || null,
            severity: severityFilter || null,
            source: sourceFilter || null,
            assigned_to: assignedToFilter || null,
        }));
        if (M7.view_all == 1) {
            dispatch(getTicketDashboard());
        }
    };

    const dateLocale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'sq' ? 'sq-AL' : 'en-GB';

    const columns = [
        {
            name: t('tickets.columns.title'),
            selector: row => row.title,
            sortable: true,
            grow: 2,
            cell: row => (
                <span style={{ fontWeight: 500, color: '#0F2744', cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/tickets/${row.ticket_id}`)}>
                    {row.title}
                </span>
            ),
        },
        {
            name: t('tickets.columns.type'),
            selector: row => row.alert_type,
            sortable: true,
            cell: row => ALERT_TYPE_KEYS[row.alert_type] ? t(ALERT_TYPE_KEYS[row.alert_type]) : row.alert_type,
            width: '140px',
        },
        {
            name: t('tickets.columns.severity'),
            selector: row => row.severity,
            sortable: true,
            cell: row => <Badge color={SEVERITY_COLORS[row.severity]}>{row.severity}</Badge>,
            width: '110px',
        },
        {
            name: t('tickets.columns.status'),
            selector: row => row.status,
            sortable: true,
            cell: row => <Badge color={STATUS_COLORS[row.status]}>{STATUS_KEYS[row.status] ? t(STATUS_KEYS[row.status]) : row.status}</Badge>,
            width: '130px',
        },
        {
            name: t('tickets.columns.source'),
            selector: row => row.source,
            sortable: true,
            cell: row => <span style={{ textTransform: 'capitalize' }}>{row.source}</span>,
            width: '90px',
        },
        {
            name: t('tickets.columns.assigned_to'),
            selector: row => row.assigned_to_name,
            sortable: true,
            cell: row => row.assigned_to_name || <span style={{ color: '#aaa' }}>{t('tickets.filters.unassigned')}</span>,
            width: '140px',
        },
        {
            name: t('tickets.columns.created'),
            selector: row => row.created_at,
            sortable: true,
            cell: row => row.created_at ? new Date(row.created_at).toLocaleDateString(dateLocale, {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : '-',
            width: '170px',
        },
        {
            name: t('tickets.columns.action'),
            cell: row => (
                <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/tickets/${row.ticket_id}`); }}
                    style={{
                        background: 'none',
                        border: '1px solid #e0e0e0',
                        borderRadius: 6,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        color: '#0F2744',
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#E60000'; e.currentTarget.style.color = '#E60000'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.color = '#0F2744'; }}
                    title={t('tickets.view_details')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                    </svg>
                    {t('tickets.view')}
                </button>
            ),
            width: '100px',
            right: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: 600,
                color: '#728095',
                backgroundColor: '#f8f9fb',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#0F2744',
            },
        },
        rows: {
            style: {
                minHeight: '52px',
            },
        },
    };

    return (
        <>
            <div className="d-flex flex-column main-outer-wrapper">
                <SurveyListHeader />
                <div className="flex-grow-1 d-flex survey-main-wrapper">
                    <SurveyListSidebar />
                    <div className="flex-grow-1 dashboard-page-outer">
                        <div className="dashboard-page-wrap" style={{ padding: '24px 32px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h4 style={{ fontWeight: 700, color: '#0F2744', margin: 0 }}>{t('tickets.title')}</h4>
                                {M7.view_own == 1 && (
                                    <button className="btn btn-danger" onClick={() => setShowCreateModal(true)}>
                                        {t('tickets.create_ticket')}
                                    </button>
                                )}
                            </div>

                            {M7.view_all == 1 && dashboard && (
                                <div className="d-flex gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
                                    <DashboardCard label={t('tickets.dashboard.open')} value={dashboard.by_status?.open || 0} color="#dc3545" />
                                    <DashboardCard label={t('tickets.dashboard.acknowledged')} value={dashboard.by_status?.acknowledged || 0} color="#17a2b8" />
                                    <DashboardCard label={t('tickets.dashboard.in_progress')} value={dashboard.by_status?.in_progress || 0} color="#ffc107" />
                                    <DashboardCard label={t('tickets.dashboard.resolved')} value={dashboard.by_status?.resolved || 0} color="#28a745" />
                                    <DashboardCard label={t('tickets.dashboard.total')} value={dashboard.total || 0} color="#0F2744" />
                                </div>
                            )}

                            <div className="d-flex gap-2 mb-3 align-items-center" style={{ flexWrap: 'wrap' }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={t('tickets.search_placeholder')}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    style={{ maxWidth: 240 }}
                                />
                                <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
                                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">{t('tickets.filters.all_statuses')}</option>
                                    <option value="open">{t('tickets.status.open')}</option>
                                    <option value="acknowledged">{t('tickets.status.acknowledged')}</option>
                                    <option value="in_progress">{t('tickets.status.in_progress')}</option>
                                    <option value="resolved">{t('tickets.status.resolved')}</option>
                                    <option value="false_positive">{t('tickets.status.false_positive')}</option>
                                </select>
                                <select className="form-select form-select-sm" style={{ maxWidth: 150 }}
                                    value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                                    <option value="">{t('tickets.filters.all_severities')}</option>
                                    <option value="low">{t('tickets.severity.low')}</option>
                                    <option value="medium">{t('tickets.severity.medium')}</option>
                                    <option value="high">{t('tickets.severity.high')}</option>
                                    <option value="critical">{t('tickets.severity.critical')}</option>
                                </select>
                                <select className="form-select form-select-sm" style={{ maxWidth: 130 }}
                                    value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                                    <option value="">{t('tickets.filters.all_sources')}</option>
                                    <option value="system">{t('tickets.source.system')}</option>
                                    <option value="user">{t('tickets.source.user')}</option>
                                </select>
                                {M7.view_all == 1 && (
                                    <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
                                        value={assignedToFilter} onChange={(e) => setAssignedToFilter(e.target.value)}>
                                        <option value="">{t('tickets.filters.all_assignees')}</option>
                                        <option value="unassigned">{t('tickets.filters.unassigned')}</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                                <DataTable
                                    columns={columns}
                                    data={filteredTickets}
                                    progressPending={loading}
                                    pagination
                                    paginationPerPage={15}
                                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                                    noDataComponent={<div style={{ padding: 40, color: '#728095' }}>{t('tickets.no_tickets')}</div>}
                                    customStyles={customStyles}
                                    highlightOnHover
                                    pointerOnHover
                                    onRowClicked={(row) => navigate(`/admin/tickets/${row.ticket_id}`)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showCreateModal && (
                <CreateTicketModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </>
    );
}
